import { api } from '../axios';
import {
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  CloneInvoiceRequest,
  InvoiceResponse,
  InvoiceListQuery,
  InvoiceLineResponse,
  PaginatedResponse,
  UpdateInvoiceLinePriceRequest
} from '@/types/invoice';

export const invoiceApi = {
  // Create a new invoice from an order
  createInvoice: async (orderId: string): Promise<InvoiceResponse> => {
    // Create invoice request with only required fields
    // Financial calculations are now handled server-side
    const createRequest: CreateInvoiceRequest = {
      order_id: orderId,
      note: ''
    };

    const response = await api.post<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>('/invoices', createRequest);

    return response.data.data;
  },

  // Get all invoices with pagination and filtering
  getInvoices: async (
    page: number = 1,
    pageSize: number = 10,
    filters: Partial<InvoiceListQuery> = {}
  ): Promise<PaginatedResponse<InvoiceResponse>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    // Add filter parameters
    if (filters.search) params.append('search', filters.search);
    if (filters.order_id) params.append('order_id', filters.order_id);
    if (filters.customer_id) params.append('customer_id', filters.customer_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get<PaginatedResponse<InvoiceResponse>>(
      `/invoices?${params.toString()}`
    );

    return response.data;
  },

  // Get a single invoice by ID with customer and invoice lines
  getInvoice: async (id: string): Promise<InvoiceResponse> => {
    const response = await api.get<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/${id}`);

    return response.data.data;
  },

  // Get a single invoice by CODE with customer and invoice lines
  getInvoiceByCode: async (code: string): Promise<InvoiceResponse> => {
    const response = await api.get<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/code/${code}`);

    return response.data.data;
  },

  // Update an existing invoice
  updateInvoice: async (id: string, data: UpdateInvoiceRequest): Promise<InvoiceResponse> => {
    const response = await api.put<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/${id}`, data);

    return response.data.data;
  },

  // Delete an invoice
  deleteInvoice: async (id: string): Promise<void> => {
    await api.delete(`/invoices/${id}`);
  },


  // Get invoice status counts
  getStatusCounts: async (filters: Partial<InvoiceListQuery> = {}): Promise<Record<string, number>> => {
    const params = new URLSearchParams();
    
    // Add filter parameters (excluding status, page, page_size, sort_by, sort_order)
    if (filters.search) params.append('search', filters.search);
    if (filters.customer_id) params.append('customer_id', filters.customer_id);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.order_id) params.append('order_id', filters.order_id);

    const response = await api.get<{
      data: Record<string, number>;
      message: string;
      timestamp: string;
    }>(`/invoices/status-counts?${params.toString()}`);

    return response.data.data;
  },

  // Cancel an invoice
  cancelInvoice: async (id: string): Promise<InvoiceResponse> => {
    const response = await api.patch<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/${id}/cancel`);

    return response.data.data;
  },

  // Confirm an invoice (Draft -> WaitingPayment)
  confirmInvoice: async (id: string): Promise<InvoiceResponse> => {
    const response = await api.patch<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/${id}/confirm`);

    return response.data.data;
  },

  // Update invoice line price (only for Draft status)
  // Formula: TotalPrice = (SellingPrice - Discount) * Quantity
  updateInvoiceLinePrice: async (
    invoiceCode: string,
    lineId: string,
    data: UpdateInvoiceLinePriceRequest
  ): Promise<InvoiceLineResponse> => {
    const response = await api.patch<{
      data: InvoiceLineResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/code/${invoiceCode}/lines/${lineId}`, data);

    return response.data.data;
  },

  // Clone an invoice with modifications
  // Only allowed for invoices in waiting_payment status that are not fully paid
  cloneInvoice: async (
    id: string,
    data: CloneInvoiceRequest
  ): Promise<InvoiceResponse> => {
    const response = await api.post<{
      data: InvoiceResponse;
      message: string;
      timestamp: string;
    }>(`/invoices/${id}/clone`, data);

    return response.data.data;
  }
};
